// Amprentele în plan ale betonului din scena 3D: stâlpi, grinzi, pereți.
//
// Două locuri au nevoie de aceeași întrebare — «ce beton e aici?»: armătura
// (cât intră bara grinzii în reazem, unde stă carcasa stâlpului) și betonul
// însuși (până unde se desenează un stâlp care intră într-un perete). Regula
// «ce amprentă are un stâlp» stă deci aici, o singură dată.

import type { FemPereteBeton, FemStalp } from "./tipuri";

export type Amprenta = [number, number][];

/** Punctul e în poligon? Regula par–impar. */
export function inPoligon(x: number, y: number, p: Amprenta): boolean {
  let e = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i];
    const [xj, yj] = p[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      e = !e;
    }
  }
  return e;
}

/** Dreptunghiul de lățime `lat` în jurul segmentului a → b. */
export function dreptunghiSegment(ax: number, ay: number, bx: number,
                                  by: number, lat: number): Amprenta {
  const L = Math.hypot(bx - ax, by - ay) || 1;
  const nx = (-(by - ay) / L) * (lat / 2);
  const ny = ((bx - ax) / L) * (lat / 2);
  return [[ax + nx, ay + ny], [bx + nx, by + ny], [bx - nx, by - ny],
    [ax - nx, ay - ny]];
}

/** Amprenta DESENATĂ a unui stâlp: cea reală din Pasul 4 când o are, altfel
 *  cutia h × b pe `dir`, centrată în nod — exact ce extrudează scena. */
export function amprentaStalp(s: FemStalp,
                              nod: { x_m: number; y_m: number }): Amprenta {
  if (s.amprenta_m && s.amprenta_m.length >= 3) return s.amprenta_m;
  const rot = s.dir ? Math.atan2(s.dir[1], s.dir[0]) : 0;
  const c = Math.cos(rot);
  const si = Math.sin(rot);
  const h = s.h_m / 2;
  const b = s.b_m / 2;
  return ([[-h, -b], [h, -b], [h, b], [-h, b]] as const).map(
    ([u, v]) => [nod.x_m + u * c - v * si, nod.y_m + u * si + v * c]);
}

/** Amprenta unui perete de beton: panoul turnat, între fețele stâlpilor. */
export function amprentaPerete(p: FemPereteBeton): Amprenta {
  return dreptunghiSegment(p.a_xy[0], p.a_xy[1], p.b_xy[0], p.b_xy[1], p.t_m);
}

/** Aria poligonului (m²), fără semn. */
export function arie(p: Amprenta) {
  let a = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    a += p[j][0] * p[i][1] - p[i][0] * p[j][1];
  }
  return Math.abs(a) / 2;
}

/** Poligonul, tăiat de dreapta `n·P = d`: rămâne partea cu `n·P ≤ d`. */
function taieSemiplan(p: Amprenta, n: [number, number], d: number): Amprenta {
  const out: Amprenta = [];
  const f = (q: [number, number]) => n[0] * q[0] + n[1] * q[1] - d;
  for (let i = 0; i < p.length; i++) {
    const a = p[i];
    const b = p[(i + 1) % p.length];
    const fa = f(a);
    const fb = f(b);
    if (fa <= 0) out.push(a);
    if ((fa < 0 && fb > 0) || (fa > 0 && fb < 0)) {
      const t = fa / (fa - fb);
      out.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);
    }
  }
  return out;
}

/** Sub atâta, capătul unui perete e «în dreptul» stâlpului: peretele nu trece
 *  de el, ci se oprește în el (colțul clădirii, capătul unui panou). */
const TRECE_M = 0.05;
/** Cât are voie stâlpul să iasă de fața de dincolo a peretelui și să fie
 *  încă «oprit în perete»: rotunjirile Pasului 4 lasă fețele la 1–2 mm. */
const IESE_M = 0.02;

/** Amprenta stâlpului FĂRĂ partea din grosimea pereților care trec prin
 *  capătul lui.
 *
 *  Pe Olteniței P+4, lamelele din interior intră cu capătul în peretele
 *  perimetral al subsolului, fiecare altfel: ST27.1 și ST29.1 se opresc la
 *  11 cm în grosimea lui PB6, ST8.1 ajunge exact în fața lui exterioară. Cum
 *  la egalitate de adâncime stâlpul câștigă (v. «cine câștigă întâlnirea a
 *  două fețe» în `StepFem`), ST8.1 ieșea pe fața peretelui ca o fâșie de
 *  altă culoare, iar vecinele lui, identice ca rol, nu — pe același zid.
 *
 *  Peretele care TRECE pe lângă stâlp, de o parte și de alta, e continuu
 *  acolo: betonul din grosimea lui e al peretelui. Stâlpul se desenează deci
 *  până la fața peretelui dinspre corpul lui, și toate lamelele se văd la fel
 *  — oprite în perete. Nu se atinge nimic altceva:
 *    · stâlpul de capăt al unui perete (peretele se oprește în el) rămâne
 *      întreg — acolo stâlpul e colțul, iar peretele e tăiat la el;
 *    · stâlpul turnat în LUNGUL peretelui (paralel cu el) rămâne întreg;
 *    · stâlpul care trece prin perete de o parte și de alta rămâne întreg —
 *      acolo nu e un capăt, e o încrucișare.
 *
 *  `null` când nu e nimic de tăiat. */
export function amprentaFaraPereti(s: FemStalp, amp: Amprenta,
                                   pereti: FemPereteBeton[]): Amprenta | null {
  const d = s.dir ?? [1, 0];
  const dl = Math.hypot(d[0], d[1]) || 1;
  let rez = amp;
  for (const p of pereti) {
    if (p.etaj !== s.etaj) continue;
    const [ax, ay] = p.a_xy;
    const L = Math.hypot(p.b_xy[0] - ax, p.b_xy[1] - ay);
    if (L < 1e-6) continue;
    const u: [number, number] = [(p.b_xy[0] - ax) / L, (p.b_xy[1] - ay) / L];
    // perpendicular pe perete: un stâlp paralel cu el e turnat în lungul lui
    if (Math.abs((d[0] * u[0] + d[1] * u[1]) / dl) > 0.5) continue;
    const n: [number, number] = [-u[1], u[0]];
    const t = p.t_m / 2;
    let sMin = Infinity; let sMax = -Infinity;
    let vMin = Infinity; let vMax = -Infinity;
    for (const [x, y] of rez) {
      const s_ = (x - ax) * u[0] + (y - ay) * u[1];
      const v = (x - ax) * n[0] + (y - ay) * n[1];
      sMin = Math.min(sMin, s_); sMax = Math.max(sMax, s_);
      vMin = Math.min(vMin, v); vMax = Math.max(vMax, v);
    }
    // peretele trece de stâlp de AMBELE părți
    if (sMin < TRECE_M || sMax > L - TRECE_M) continue;
    // capătul stâlpului e în grosimea peretelui (cel mult `IESE_M` dincolo de
    // fața opusă corpului), corpul lui de o singură parte; `a` e capătul,
    // măsurat cu semnul care pune corpul spre +
    const cadeInPerete = (a: number) => a > -t - IESE_M && a < t;
    let taiat: Amprenta | null = null;
    const c = ax * n[0] + ay * n[1];
    if (vMax > t && cadeInPerete(vMin)) {
      // corpul e de partea +n: rămâne ce e dincolo de fața +n
      taiat = taieSemiplan(rez, [-n[0], -n[1]], -(c + t));
    } else if (vMin < -t && cadeInPerete(-vMax)) {
      taiat = taieSemiplan(rez, n, c - t);
    }
    // o bucată mai mică decât jumătate din stâlp nu e «corpul» lui: acolo
    // stâlpul e mai gros decât peretele și doar îl depășește pe o față
    if (taiat && taiat.length >= 3 && arie(taiat) > 0.5 * arie(rez)) {
      rez = taiat;
    }
  }
  return rez === amp ? null : rez;
}
